import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Crown, Activity, LineChart, Landmark, Brain, Boxes, Leaf, Coins, ScrollText,
  Trophy, Library, Sparkles, FlaskConical, Building2, Waves, Cable, Gem, Trees,
  Radar, GitBranch, ShieldAlert, Globe, Command, PlayCircle, Loader2, CheckCircle2,
  XCircle, BadgeCheck,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Module registry (matches backend). Order preserves the executive narrative.
// ---------------------------------------------------------------------------
const MODULES = [
  { key: "situation_room",           label: "National Resource Situation Room™",           icon: Activity,      category: "core" },
  { key: "economy",                  label: "Resource Economy Engine™",                   icon: LineChart,     category: "core" },
  { key: "marketplace",              label: "National Investment Marketplace™",           icon: Landmark,      category: "core" },
  { key: "geo_land_ai",              label: "National Geological & Land AI™",             icon: Brain,         category: "core" },
  { key: "twin",                     label: "National Resource Digital Twin™",            icon: Boxes,         category: "core" },
  { key: "sustainability",           label: "Sustainability Intelligence™",               icon: Leaf,          category: "core" },
  { key: "revenue_intel",            label: "Resource Revenue Intelligence™",             icon: Coins,         category: "core" },
  { key: "policy_intel",             label: "National Policy Intelligence™",              icon: ScrollText,    category: "core" },
  { key: "benchmarking",             label: "Global Benchmarking Centre™",                icon: Trophy,        category: "core" },
  { key: "knowledge_repo",           label: "National Resource Knowledge™",               icon: Library,       category: "core" },
  { key: "decision_support",         label: "Executive Decision Support™",                icon: Sparkles,      category: "core" },
  { key: "simulation",               label: "National Simulation Laboratory™",            icon: FlaskConical,  category: "core" },
  { key: "cadastre_3d",              label: "National 3D Cadastre™",                      icon: Building2,     category: "global" },
  { key: "marine",                   label: "Marine Cadastre™",                           icon: Waves,         category: "global" },
  { key: "infrastructure",           label: "National Infrastructure Cadastre™",          icon: Cable,         category: "global" },
  { key: "critical_minerals",        label: "Critical Minerals Command Centre™",          icon: Gem,           category: "global" },
  { key: "carbon_natural",           label: "Carbon & Natural Capital Cadastre™",         icon: Trees,         category: "global" },
  { key: "forecasting",              label: "AI Resource Forecasting™",                   icon: Radar,         category: "global" },
  { key: "permit_lifecycle",         label: "National Permit Lifecycle™",                 icon: GitBranch,     category: "global" },
  { key: "risk_centre",              label: "Sovereign Risk Centre™",                     icon: ShieldAlert,   category: "global" },
  { key: "international_compliance", label: "International Compliance Centre™",           icon: Globe,         category: "global" },
  { key: "command_centre",           label: "National Resource Command Centre™",          icon: Command,       category: "flagship" },
  { key: "acceptance",               label: "Acceptance & Governance",                    icon: BadgeCheck,    category: "governance" },
];

// ---------------------------------------------------------------------------
// Small display primitives (reuse existing cad-* utility classes)
// ---------------------------------------------------------------------------
const KV = ({ k, v }) => (
  <div className="border-b cad-hairline pb-1 flex items-center gap-2 text-[12px]">
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5] flex-1 truncate">{k}</div>
    <div className="font-mono-cad text-[#F6F8FA] break-all text-right max-w-[65%]">
      {typeof v === "object" ? JSON.stringify(v) : String(v)}
    </div>
  </div>
);

const Section = ({ title, children, testId }) => (
  <div className="cad-panel p-4" data-testid={testId}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">{title}</div>
    <div className="mt-3 space-y-1">{children}</div>
  </div>
);

const MetricTile = ({ label, value, testId }) => (
  <div className="cad-panel px-3 py-2" data-testid={testId}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className="font-display font-bold text-[#F6F8FA] mt-0.5 text-[15px] break-words">
      {typeof value === "boolean" ? (value ? "yes" : "no") : String(value)}
    </div>
  </div>
);

// Renders any JSON payload as a coherent panel grid.
const AutoPanel = ({ data }) => {
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {Object.entries(data).map(([key, val]) => {
        if (key === "generated_at") return null;
        if (Array.isArray(val)) {
          return (
            <Section key={key} title={key.replace(/_/g, " ")}>
              <div className="space-y-1 max-h-[340px] overflow-y-auto">
                {val.slice(0, 40).map((item, i) => (
                  <div key={key + "-" + i} className="border-b cad-hairline pb-1 text-[11px] text-[#DCE3E8]">
                    {typeof item === "object" && item !== null ? (
                      <div className="grid grid-cols-2 gap-1">
                        {Object.entries(item).slice(0, 10).map(([kk, vv]) => (
                          <div key={kk} className="truncate">
                            <span className="font-mono-cad text-[10px] text-[#7C93A5]">{kk}</span>{" "}
                            <span className="text-[#F6F8FA]">
                              {typeof vv === "object" ? JSON.stringify(vv) : String(vv)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : String(item)}
                  </div>
                ))}
                {val.length > 40 && (
                  <div className="text-[10px] font-mono-cad text-[#7C93A5]">…{val.length - 40} more</div>
                )}
              </div>
            </Section>
          );
        }
        if (typeof val === "object" && val !== null) {
          return (
            <Section key={key} title={key.replace(/_/g, " ")}>
              {Object.entries(val).slice(0, 32).map(([kk, vv]) => <KV key={kk} k={kk} v={vv} />)}
            </Section>
          );
        }
        return <MetricTile key={key} label={key.replace(/_/g, " ")} value={val} />;
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview banner — three-pillar reminder + module registry
// ---------------------------------------------------------------------------
const OverviewBanner = () => {
  const q = useQuery({
    queryKey: ["sic-overview"],
    queryFn: async () => (await api.get("/cadastre360/sovereign/overview")).data,
  });
  const d = q.data;
  return (
    <div className="cad-panel p-4" data-testid="sic-overview">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(d?.pillars || [
          { key: "land", label: "National Land Cadastre" },
          { key: "mining", label: "National Mining Cadastre" },
          { key: "survey", label: "National Survey Cadastre" },
        ]).map((p) => (
          <div key={p.key} className="border cad-hairline rounded-sm p-3">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Pillar</div>
            <div className="font-display font-bold text-[#F6F8FA] mt-0.5">{p.label}</div>
            <div className="text-[11px] text-[#B3C2CC] mt-1">
              records: <span className="font-mono-cad text-[#F6F8FA]">{p.records ?? "—"}</span>
              {p.active != null && (<> · active: <span className="font-mono-cad text-[#F6F8FA]">{p.active}</span></>)}
            </div>
          </div>
        ))}
      </div>
      {d?.positioning && (
        <div className="mt-3 text-[12px] text-[#B3C2CC] italic border-l-2 border-[#C99A2E] pl-3">
          {d.positioning}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Decision Support — invokes AI briefing endpoint
// ---------------------------------------------------------------------------
const DecisionSupportPanel = () => {
  const [audience, setAudience] = useState("cabinet");
  const [focus, setFocus] = useState("");
  const [brief, setBrief] = useState(null);
  const gen = useMutation({
    mutationFn: async () =>
      (await api.post("/cadastre360/sovereign/decision-support/brief", { audience, focus })).data,
    onSuccess: (d) => { setBrief(d); toast.success("Executive briefing generated"); },
    onError: () => toast.error("Briefing generation failed"),
  });
  return (
    <div className="space-y-3" data-testid="sic-decision-support">
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">
          Cabinet-grade Executive Briefing
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5] mb-1">Audience</div>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              data-testid="sic-brief-audience"
              className="w-full bg-[#0B3558]/40 border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]"
            >
              <option value="cabinet">Cabinet of Zimbabwe</option>
              <option value="minister">Honourable Minister</option>
              <option value="commissioner">National Cadastre Commissioner</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5] mb-1">Focus (optional)</div>
            <input
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              data-testid="sic-brief-focus"
              placeholder="e.g. lithium beneficiation strategy"
              className="w-full bg-[#0B3558]/40 border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]"
            />
          </div>
        </div>
        <div className="mt-3">
          <button
            onClick={() => gen.mutate()}
            disabled={gen.isPending}
            data-testid="sic-brief-generate"
            className="cad-btn-primary px-4 py-2 text-[11px] rounded-sm inline-flex items-center gap-1"
          >
            {gen.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Generate briefing
          </button>
        </div>
      </div>
      {brief && (
        <div className="cad-panel p-4" data-testid="sic-brief-output">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">
            {brief.audience} · {brief.engine}
          </div>
          <div className="mt-2 text-[13px] text-[#F6F8FA] leading-relaxed whitespace-pre-wrap">
            {brief.brief}
          </div>
          {brief.snapshot && (
            <div className="mt-3 text-[11px] font-mono-cad text-[#7C93A5] border-t cad-hairline pt-2">
              Snapshot: {brief.snapshot}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Policy Simulation Panel — POST with parameters
// ---------------------------------------------------------------------------
const PolicySimulationPanel = () => {
  const [scenario, setScenario] = useState("Balanced Growth 2030");
  const [royalty, setRoyalty] = useState(2);
  const [tax, setTax] = useState(0);
  const [licence, setLicence] = useState(0);
  const [incentive, setIncentive] = useState(5);
  const [horizon, setHorizon] = useState(5);
  const [result, setResult] = useState(null);
  const run = useMutation({
    mutationFn: async () =>
      (await api.post("/cadastre360/sovereign/policy-sim", {
        scenario, royalty_change_pct: royalty, tax_change_pct: tax,
        licence_fee_change_pct: licence, investment_incentive_pct: incentive,
        horizon_years: horizon,
      })).data,
    onSuccess: (d) => { setResult(d); toast.success("Policy scenario evaluated"); },
    onError: () => toast.error("Simulation failed"),
  });
  return (
    <div className="space-y-3" data-testid="sic-policy-sim">
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">
          National Policy Impact Simulator
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5] mb-1">Scenario</div>
            <input
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              data-testid="sic-policy-scenario"
              className="w-full bg-[#0B3558]/40 border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]"
            />
          </div>
          {[
            ["Royalty Δ%",  royalty, setRoyalty, "sic-policy-royalty"],
            ["Tax Δ%",       tax,     setTax,     "sic-policy-tax"],
            ["Licence Fee Δ%", licence, setLicence, "sic-policy-licence"],
            ["Investment Incentive %", incentive, setIncentive, "sic-policy-incentive"],
            ["Horizon (years)", horizon, setHorizon, "sic-policy-horizon"],
          ].map(([label, val, setter, tid]) => (
            <div key={label}>
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5] mb-1">{label}</div>
              <input
                type="number"
                value={val}
                onChange={(e) => setter(Number(e.target.value))}
                data-testid={tid}
                className="w-full bg-[#0B3558]/40 border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono-cad"
              />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <button
            onClick={() => run.mutate()}
            disabled={run.isPending}
            data-testid="sic-policy-run"
            className="cad-btn-primary px-4 py-2 text-[11px] rounded-sm inline-flex items-center gap-1"
          >
            {run.isPending ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
            Run policy simulation
          </button>
        </div>
      </div>
      {result && (
        <>
          <div className="cad-panel p-4">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Recommendation</div>
            <div className="mt-1 text-[14px] text-[#F6F8FA] font-display font-bold">
              {result.cabinet_recommendation}
            </div>
          </div>
          <AutoPanel data={result} />
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Simulation Laboratory — POST scenario
// ---------------------------------------------------------------------------
const SimulationLabPanel = () => {
  const [scenario, setScenario] = useState("new_discovery");
  const [horizon, setHorizon] = useState(5);
  const [result, setResult] = useState(null);
  const run = useMutation({
    mutationFn: async () =>
      (await api.post("/cadastre360/sovereign/simulation", {
        scenario, parameters: {}, horizon_years: horizon,
      })).data,
    onSuccess: (d) => { setResult(d); toast.success("Simulation complete"); },
    onError: () => toast.error("Simulation failed"),
  });
  const scenarios = [
    "commodity_price_shock", "royalty_increase", "mine_closure", "new_discovery",
    "drought", "new_highway", "population_growth", "new_industrial_zone",
    "carbon_policy", "investment_incentive",
  ];
  return (
    <div className="space-y-3" data-testid="sic-sim-lab">
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">
          National Simulation Laboratory
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5] mb-1">Scenario</div>
            <select
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              data-testid="sic-sim-scenario"
              className="w-full bg-[#0B3558]/40 border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]"
            >
              {scenarios.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5] mb-1">Horizon (years)</div>
            <input
              type="number"
              value={horizon}
              onChange={(e) => setHorizon(Number(e.target.value))}
              data-testid="sic-sim-horizon"
              className="w-full bg-[#0B3558]/40 border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono-cad"
            />
          </div>
        </div>
        <div className="mt-3">
          <button
            onClick={() => run.mutate()}
            disabled={run.isPending}
            data-testid="sic-sim-run"
            className="cad-btn-primary px-4 py-2 text-[11px] rounded-sm inline-flex items-center gap-1"
          >
            {run.isPending ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />}
            Run simulation
          </button>
        </div>
      </div>
      {result && <AutoPanel data={result} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Acceptance Panel — governance evidence
// ---------------------------------------------------------------------------
const AcceptancePanel = () => {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ["sic-acc"],
    queryFn: async () => (await api.get("/cadastre360/sovereign/acceptance/reports")).data,
  });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/sovereign/acceptance/run")).data,
    onSuccess: (d) => {
      toast.success(`Phase VII ${d.overall} · ${d.passed}/${d.scope_size}`);
      qc.invalidateQueries({ queryKey: ["sic-acc"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Acceptance requires admin role"),
  });
  const latest = (reports.data || [])[0];
  return (
    <div className="space-y-3" data-testid="sic-acceptance">
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[240px]">
          Verify Phase VII additive compliance across all Sovereign Intelligence Centre modules
          and global leadership capabilities. Confirms zero regression to Land, Mining and Survey pillars.
        </div>
        <button
          onClick={() => run.mutate()}
          disabled={run.isPending}
          data-testid="sic-acceptance-run"
          className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
        >
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run acceptance
        </button>
      </div>
      {latest && (
        <div className="cad-panel p-4" data-testid="sic-acceptance-latest">
          <div className="flex items-center gap-3">
            <span className={"cad-chip text-[10px] uppercase " + (latest.overall === "PASS" ? "cad-chip-green" : "cad-chip-red")}>
              {latest.overall}
            </span>
            <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{latest.passed}/{latest.scope_size}</span>
            <span className="font-mono-cad text-[10px] text-[#7C93A5] ml-auto">
              {latest.run_at?.slice(0, 19).replace("T", " ")}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1 max-h-[420px] overflow-y-auto">
            {(latest.checks || []).map((c, i) => (
              <div key={`chk-${latest.id}-${i}`} className="border-b cad-hairline pb-1 flex items-start gap-2">
                {c.ok
                  ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" />
                  : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
                <div>
                  <div className="text-[11px] text-[#F6F8FA]">{c.label}</div>
                  <div className="text-[10px] text-[#7C93A5] font-mono-cad">{c.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Generic module content renderer
// ---------------------------------------------------------------------------
const GET_ENDPOINTS = {
  situation_room:           "/cadastre360/sovereign/situation-room",
  economy:                  "/cadastre360/sovereign/economy",
  marketplace:              "/cadastre360/sovereign/marketplace",
  geo_land_ai:              "/cadastre360/sovereign/geo-land-ai",
  twin:                     "/cadastre360/sovereign/twin",
  sustainability:           "/cadastre360/sovereign/sustainability",
  revenue_intel:            "/cadastre360/sovereign/revenue-intel",
  benchmarking:             "/cadastre360/sovereign/benchmarking",
  knowledge_repo:           "/cadastre360/sovereign/knowledge-repo",
  cadastre_3d:              "/cadastre360/sovereign/3d-cadastre",
  marine:                   "/cadastre360/sovereign/marine",
  infrastructure:           "/cadastre360/sovereign/infrastructure",
  critical_minerals:        "/cadastre360/sovereign/critical-minerals",
  carbon_natural:           "/cadastre360/sovereign/carbon-natural-capital",
  forecasting:              "/cadastre360/sovereign/forecast",
  permit_lifecycle:         "/cadastre360/sovereign/permit-lifecycle",
  risk_centre:              "/cadastre360/sovereign/risk-centre",
  international_compliance: "/cadastre360/sovereign/international-compliance",
  command_centre:           "/cadastre360/sovereign/command-centre",
};

const AutoModule = ({ moduleKey }) => {
  const q = useQuery({
    queryKey: ["sic-" + moduleKey],
    queryFn: async () => (await api.get(GET_ENDPOINTS[moduleKey])).data,
    enabled: Boolean(GET_ENDPOINTS[moduleKey]),
  });
  if (q.isLoading) return <div className="cad-panel p-6 text-[#B3C2CC] text-[13px]">Loading…</div>;
  if (q.isError)   return <div className="cad-panel p-6 text-[#E67E7E] text-[13px]">Module unavailable</div>;
  return <AutoPanel data={q.data} />;
};

// ---------------------------------------------------------------------------
// Root page
// ---------------------------------------------------------------------------
export const SovereignIntelligenceCentre = () => {
  const [tab, setTab] = useState("situation_room");
  const activeMod = MODULES.find((m) => m.key === tab);

  const renderContent = () => {
    if (tab === "decision_support") return <DecisionSupportPanel />;
    if (tab === "policy_intel")     return <PolicySimulationPanel />;
    if (tab === "simulation")       return <SimulationLabPanel />;
    if (tab === "acceptance")       return <AcceptancePanel />;
    return <AutoModule moduleKey={tab} />;
  };

  return (
    <div className="p-6 space-y-4" data-testid="page-sovereign-intelligence">
      <div className="flex items-start gap-3">
        <Crown size={32} className="text-[#C99A2E]" />
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
            Cadastre360 · Phase VII · Sovereign Intelligence Centre v1.7
          </div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-0.5">
            National Strategic Intelligence over Land, Mining & Survey Cadastres
          </h1>
          <div className="text-[12.5px] text-[#B3C2CC] mt-1 max-w-4xl">
            A new capability WITHIN Cadastre360 that consumes the three sovereign pillars
            without altering them. Zero regression · reuses existing enterprise services.
          </div>
        </div>
      </div>

      <OverviewBanner />

      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1 sticky top-2 z-10" data-testid="sic-tabs">
        {MODULES.map((m) => {
          const Icon = m.icon;
          const active = tab === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setTab(m.key)}
              data-testid={"sic-tab-" + m.key}
              className={
                "px-2.5 py-1.5 rounded-sm text-[10.5px] font-mono-cad uppercase tracking-[0.12em] " +
                "inline-flex items-center gap-1.5 " +
                (active ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")
              }
            >
              <Icon size={11} className="text-[#C99A2E]" /> {m.label}
            </button>
          );
        })}
      </div>

      {activeMod && (
        <div className="cad-panel p-3 flex items-center gap-3" data-testid="sic-active-header">
          <activeMod.icon size={18} className="text-[#C99A2E]" />
          <div className="font-display text-lg font-bold text-[#F6F8FA]">{activeMod.label}</div>
          <span className="cad-chip text-[10px] uppercase font-mono-cad ml-auto">
            {activeMod.category}
          </span>
        </div>
      )}

      {renderContent()}
    </div>
  );
};

export default SovereignIntelligenceCentre;
